/**
 * How a join ticket gets from the shell to the arena.
 *
 * The two are separate pages — the match owns its own canvas, its own fixed-step loop and a
 * 3 MB bundle, and mounting it inside the SPA would put Three.js and Rapier behind every login
 * screen. So the shell mints the ticket (it is the page with a session) and the arena spends
 * it, and something has to carry it across a navigation.
 *
 * `sessionStorage`, not a query parameter. A ticket is a bearer credential: in a URL it lands
 * in browser history, in the referer header of anything the page loads, and in any access log
 * between here and the server. It is only valid for 30 seconds, which lowers the damage
 * without changing the principle.
 *
 * Removed as it is read, because a ticket is single-use anyway — the room burns the nonce, so
 * a second attempt with the same one is refused. Leaving it behind would only mean a refresh
 * presents a ticket the server has already rejected, which reads as a broken game rather than
 * as an expired credential.
 *
 * **The arena must work without one.** `make play`, every acceptance harness and the headless
 * render check all join with no session at all, and a room with no ticket secret configured
 * runs open. So a missing ticket is the normal case, not an error.
 */
const KEY = 'blackline:match-ticket';

export interface StoredTicket {
  ticket: string;
  /** ISO timestamp from the meta tier, for the staleness check below. */
  expiresAt: string;
}

export function stashTicket(value: StoredTicket): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Private browsing and some embedded webviews refuse sessionStorage. Failing to stash a
    // ticket must not stop a player launching the game — they join the open room instead.
  }
}

/**
 * Reads and clears the stashed ticket, or null when there is none or it is stale.
 *
 * Expiry is checked here as well as on the server, not instead of it. The server's check is
 * the one that matters; this one exists so a player who left the tab open for a minute sees
 * the arena connect normally rather than watching a join get rejected for a reason no message
 * explains.
 */
export function takeTicket(now: number = Date.now()): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const stored = JSON.parse(raw) as StoredTicket;
    if (typeof stored.ticket !== 'string' || stored.ticket.length === 0) return null;
    const expiresAt = Date.parse(stored.expiresAt);
    // An unparseable expiry is treated as usable rather than discarded: the server will
    // reject it if it really is stale, and throwing away a possibly-good credential over a
    // date format is the worse failure.
    if (Number.isFinite(expiresAt) && expiresAt <= now) return null;
    return stored.ticket;
  } catch {
    return null;
  }
}
